import { addDays, addMinutes, differenceInMinutes, startOfDay } from "date-fns";
import { isEmpty, isEqual, xorWith } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type {
  BookableAssetDetailDto,
  BookableAssetUpdateBookingsToBeCancelledDto,
  BookableAssetUpdateBookingsToBeCancelledRequest,
  ExternalBookingProviderDto,
  JimRoomDto,
} from "@/api/types";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDocumentInput } from "@/components/Form/FormDocumentInput";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormScheduleInput } from "@/components/Form/FormScheduleInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { ClockCheckIcon } from "@/components/Icons/ClockCheckIcon";
import { ClockRefreshIcon } from "@/components/Icons/ClockRefreshIcon";
import { CurrencyEuroIcon } from "@/components/Icons/CurrencyEuroIcon";
import { LayerSingleIcon } from "@/components/Icons/LayerSingleIcon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { useSuggestedEmailFix } from "@/helpers/email-validation";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { getFlagIcon } from "@/helpers/flags";
import {
  createDocumentSizeRule,
  createDocumentTypeRule,
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import type { BookableDay, Specification, TimeSlot } from "@/modules/bookings/constants";
import { timeSlotOptions } from "@/modules/bookings/constants";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { AssetBookableDaySelector } from "./components/AssetBookableDaySelector";
import { AssetCustomTimeslotInput } from "./components/AssetCustomTimeslotInput";
import { ExtraSpecificationsModal } from "./components/ExtraSpecificationsModal";
import { formatBookableDaysPayload } from "./helpers";
import type { AssetObserver, CreateOrEditAssetFormValues } from "./types";

export interface BookableAssetFormProps {
  asset: BookableAssetDetailDto | undefined;
  assetObservers: AssetObserver[];
  getBookingsToBeCancelled: (payload: {
    id: string;
    data: BookableAssetUpdateBookingsToBeCancelledRequest;
  }) => Promise<BookableAssetUpdateBookingsToBeCancelledDto>;
  onSubmit: (data: CreateOrEditAssetFormValues) => Promise<void>;
  isSubmitting: boolean;
  isEditMode: boolean;
  canEditTimeslot: boolean;
  futureBookings: number;
  defaultValues: CreateOrEditAssetFormValues;
  isPublished: boolean;
  enabledExternalProviders: ExternalBookingProviderDto[];
  jimRooms: JimRoomDto[];
}

const MIN_LENGTH = {
  NAME: 5,
  DESCRIPTION: 5,
};
const MAX_LENGTH = {
  NAME: 255,
  DESCRIPTION: 25000,
};
const MAX_ADVANCE_BOOKING_DAYS = 365;
const MIN_ADVANCE_BOOKING_DAYS = 0;
const MIN_EDIT_BOOKING_HOURS = 0;
const MIN_AMOUNT_SEATS = 1;

export const BookableAssetForm = ({
  asset,
  assetObservers,
  getBookingsToBeCancelled,
  onSubmit,
  isSubmitting,
  isEditMode,
  canEditTimeslot,
  futureBookings,
  defaultValues,
  isPublished,
  enabledExternalProviders,
  jimRooms,
}: BookableAssetFormProps): React.ReactNode => {
  const [currExternalSyncStatus, setCurrExternalSyncStatus] = useState<
    BookableAssetDetailDto["externalSyncStatus"] | undefined
  >(asset?.externalSyncStatus);
  const [isExtraSpecificationsModalOpened, extraSpecificationsModalOpenHandler] = useBool(false);
  const [isCancelModalOpen, cancelModalHandler] = useBool(false);
  const [bookingsToBeCancelled, setBookingsToBeCancelled] = useState<number | null>(null);

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const postHog = usePostHog();
  const checkExternalSyncStatusMutation = bookableAssetMutations.useCheckExternalSyncStatus();

  const form = useForm<CreateOrEditAssetFormValues>({
    defaultValues,
  });
  const availableSpecifications = useWatch({
    control: form.control,
    name: "specifications",
  });
  const regulationsApplicable = useWatch({
    control: form.control,
    name: "regulationsApplicable",
  });
  const bookableDays = useWatch({
    control: form.control,
    name: "bookableDays",
  });
  const notificationEnabled = useWatch({
    control: form.control,
    name: "notificationEnabled",
  });
  const assetObserver = useWatch({
    control: form.control,
    name: "assetObserver",
  });
  const audience = useWatch({
    control: form.control,
    name: "audience",
  });
  const [minScheduled] = useState(() => addMinutes(new Date(), 5));
  const seats = useWatch({
    control: form.control,
    name: "seats",
  });
  const slot = useWatch({
    control: form.control,
    name: "timeslot",
  });
  const customTimeslot = useWatch({
    control: form.control,
    name: "customTimeslot",
  });
  const externalBookingProvider = useWatch({
    control: form.control,
    name: "externalBookingProvider",
  });
  const externalResourceId = useWatch({
    control: form.control,
    name: "externalResourceId",
  });
  const currSuggestedEmail = useSuggestedEmailFix(
    externalBookingProvider === "outlook" ? externalResourceId || "" : "",
  );

  useEffect(() => {
    form.register("bookableDays", {
      validate: {
        required: (bookableDays) => {
          if (bookableDays.filter((x) => x.enabled).length === 0) {
            return t("components.form.error.required", {
              inputName: t("page.bookings.create-or-edit.form.section.booking-details.bookable-days.name"),
            });
          }

          return undefined;
        },
        everyBookableTimeFilled: (bookableDays) => {
          const availableBookableDays = bookableDays.filter((x) => x.enabled);
          for (const bookableDay of availableBookableDays) {
            const isEveryBookableTimeFilled = bookableDay.times.every((bookableTime) => {
              return isDefined(bookableTime.startTime) && isDefined(bookableTime.endTime);
            });
            if (!isEveryBookableTimeFilled) {
              return "";
            }
          }

          return undefined;
        },
        noOverlappingtimes: (bookableDays) => {
          const availableBookableDays = bookableDays.filter((x) => x.enabled);
          for (const bookableDay of availableBookableDays) {
            const isAnyOverlappingBookableTime = bookableDay.times.some((bookableTimeA, indexA) => {
              if (!isDefined(bookableTimeA.startTime) || !isDefined(bookableTimeA.endTime)) {
                return false;
              }
              const startTimeA = bookableTimeA.startTime;
              const endTimeA = bookableTimeA.endTime;

              return bookableDay.times.some((bookableTimeB, indexB) => {
                if (indexA === indexB) {
                  return false;
                }
                if (!isDefined(bookableTimeB.startTime) || !isDefined(bookableTimeB.endTime)) {
                  return false;
                }
                const startTimeB = bookableTimeB.startTime;
                const endTimeB = bookableTimeB.endTime;

                return (
                  (startTimeA >= startTimeB && startTimeA < endTimeB) ||
                  (endTimeA > startTimeB && endTimeA <= endTimeB) ||
                  (startTimeA <= startTimeB && endTimeA >= endTimeB)
                );
              });
            });
            if (isAnyOverlappingBookableTime) {
              return t(
                "page.bookings.create-or-edit.form.section.booking-details.bookable-days.error.overlapping-time-periods",
              );
            }
          }

          return undefined;
        },
        startTimeSoonerThanEndTime: (bookableDays) => {
          const availableBookableDays = bookableDays.filter((x) => x.enabled);
          for (const bookableDay of availableBookableDays) {
            const isAnyStartTimeSoonerThanEndTime = bookableDay.times.some((bookableTime) => {
              return (
                isDefined(bookableTime.startTime) &&
                isDefined(bookableTime.endTime) &&
                bookableTime.startTime >= bookableTime.endTime
              );
            });
            if (isAnyStartTimeSoonerThanEndTime) {
              return t(
                "page.bookings.create-or-edit.form.section.booking-details.bookable-days.error.start-time-sooner-than-end-time",
              );
            }
          }

          return undefined;
        },
      },
    });
  }, [form, t]);

  const handleAddSpecifications = (specifications: Specification[]) => {
    form.setValue("specifications", specifications);
  };

  const handleDeleteSpecification = (specification: Specification) => {
    form.setValue(
      "specifications",
      availableSpecifications.filter((x) => x !== specification),
    );
    form.setValue(specification, undefined);
  };

  const areBookableTimesDifferent = (timesA: BookableDay["times"], timesB: BookableDay["times"]) => {
    return isEmpty(xorWith(timesA, timesB, isEqual));
  };

  const handleSubmitEdit = async (data: CreateOrEditAssetFormValues) => {
    const prevBookableDays = defaultValues.bookableDays;
    const newBookableDays = [...data.bookableDays];
    let isEdited = false;
    const timeOfDefaultAvailableFrom = defaultValues.availableFrom?.getTime() ?? 0;
    const timeOfDataAvailableFrom = data.availableFrom?.getTime() ?? 0;
    const timeOfDefaultUnpublishAt = defaultValues.unpublishAt?.getTime() ?? 0;
    const timeOfDataUnpublishAt = data.unpublishAt?.getTime() ?? 0;
    if (timeOfDefaultAvailableFrom != timeOfDataAvailableFrom) {
      isEdited = true;
    }
    if (timeOfDefaultUnpublishAt != timeOfDataUnpublishAt) {
      isEdited = true;
    }
    for (let i = 0; i < prevBookableDays.length; i++) {
      if (
        prevBookableDays[i].allDay !== newBookableDays[i].allDay ||
        prevBookableDays[i].enabled !== newBookableDays[i].enabled ||
        areBookableTimesDifferent(prevBookableDays[i].times, newBookableDays[i].times)
      ) {
        isEdited = true;
        break;
      }
    }
    if (!isEqual(defaultValues.audience, data.audience)) {
      isEdited = true;
    }
    let affectedBookings: BookableAssetUpdateBookingsToBeCancelledDto | undefined;
    if (isEdited) {
      affectedBookings = await getBookingsToBeCancelled({
        id: asset!.id,
        data: {
          audience: data.audience,
          availableFrom: data.availableFrom ? data.availableFrom.toISOString() : undefined,
          unpublishAt: data.unpublishAt ? data.unpublishAt.toISOString() : undefined,
          bookableDays: formatBookableDaysPayload(newBookableDays),
          seats: data.seats,
        },
      });
    }
    if (affectedBookings && affectedBookings.bookingsToBeCancelled > 0) {
      setBookingsToBeCancelled(affectedBookings.bookingsToBeCancelled);

      return;
    }
    await onSubmit(data);
  };

  const handleManualSync = () => {
    if (!asset?.id) {
      return;
    }

    checkExternalSyncStatusMutation.mutate(
      { assetId: asset?.id },
      {
        onSettled: (res) => {
          const status = res?.data.status || "failed";

          setCurrExternalSyncStatus(status);

          if (status === "failed" || status === "unknown") {
            form.setError("externalResourceId", {
              message: res?.data.message,
            });
          }
        },
      },
    );
  };

  const handleChangeTime = (
    bookableDayIndex: number,
    bookableTimeIndex: number,
    value: Date | undefined,
    type: "start" | "end",
  ) => {
    if (!value) {
      return;
    }
    const startOfDayDate = startOfDay(value);
    if (type === "end") {
      let formattedValue = value;
      // Handle "00:00:00" end of day time
      if (differenceInMinutes(value, startOfDayDate) === 0) {
        formattedValue = addDays(value, 1);
      }
      form.setValue(
        `bookableDays.${bookableDayIndex}.times.${bookableTimeIndex}.endTime`,
        differenceInMinutes(formattedValue, startOfDayDate),
      );
    } else {
      form.setValue(
        `bookableDays.${bookableDayIndex}.times.${bookableTimeIndex}.startTime`,
        differenceInMinutes(value, startOfDayDate),
      );
    }
  };

  const getTimeslotOptionLabel = (timeslot: TimeSlot | undefined) => {
    switch (timeslot) {
      case "fifteenMinutes":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.fifteen-min");
      case "thirtyMinutes":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.thirty-min");
      case "thirtyFiveMinutes":
        return t(
          "page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.thirty-five-min",
        );
      case "oneHour":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.one-hour");
      case "twoHours":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.two-hour");
      case "threeHours":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.three-hour");
      case "fourHours":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.four-hour");
      case "allDay":
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.full-day");
      default:
        return t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.custom");
    }
  };

  const title = isEditMode
    ? t("page.bookings.create-or-edit-asset.edit.title")
    : t("page.bookings.create-or-edit-asset.create.title");
  const flagIcon = getFlagIcon({
    languageId: asset?.originalLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });
  const canExternalMappingBeEdited = futureBookings === 0;
  const externalProviderOptions = [
    "none",
    ...enabledExternalProviders.filter((provider) => provider !== "none"),
    ...(defaultValues.externalBookingProvider !== "none" &&
    !enabledExternalProviders.includes(defaultValues.externalBookingProvider)
      ? [defaultValues.externalBookingProvider]
      : []),
  ] satisfies ExternalBookingProviderDto[];
  const jimRoomIds = jimRooms.map((room) => room.id);
  const jimRoomOptions =
    defaultValues.externalBookingProvider === "jim" &&
    defaultValues.externalResourceId &&
    !jimRoomIds.includes(defaultValues.externalResourceId)
      ? [...jimRoomIds, defaultValues.externalResourceId]
      : jimRoomIds;

  const getExternalProviderLabel = (provider: ExternalBookingProviderDto) =>
    t(`page.bookings.create-or-edit.form.section.asset-details.external-provider.option.${provider}`);

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.bookings.list-assets.title"),
              to: routes.bookings.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={form} onSubmit={isEditMode ? handleSubmitEdit : onSubmit}>
        <Form.Container>
          <Form.Body>
            <Notice
              icon={<Translate01Icon />}
              variant="special"
              message={
                isEditMode
                  ? t("common.label.you-are-editing-in-original-content-language")
                  : t("common.label.your-input-will-be-auto-translated")
              }
            />
            {/* Basic info section */}
            <span className="text-heading-3">{t("page.bookings.create-or-edit.form.section.asset-details")}</span>
            <FormField
              label={t("page.bookings.create-or-edit.form.section.asset-details.name.label-generic")}
              actions={flagIcon}
              required
            >
              <FormInput<CreateOrEditAssetFormValues>
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(
                      t,
                      "page.bookings.create-or-edit.form.section.asset-details.name.label-generic",
                    ),
                  },
                  minLength: {
                    message: t("components.form.error.min-length", {
                      length: MIN_LENGTH.NAME,
                    }),
                    value: MIN_LENGTH.NAME,
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH.NAME,
                    }),
                    value: MAX_LENGTH.NAME,
                  },
                }}
                placeholder={t("page.bookings.create-or-edit.form.section.asset-details.name.placeholder")}
              />
            </FormField>
            <FormField
              label={t("page.bookings.create-or-edit.form.section.asset-details.description.label-generic")}
              actions={flagIcon}
              required
            >
              <FormTextArea<CreateOrEditAssetFormValues>
                name="description"
                rules={{
                  validate: {
                    required: createRequiredStringRule(
                      t,
                      "page.bookings.create-or-edit.form.section.asset-details.description.label-generic",
                    ),
                  },
                  minLength: {
                    message: t("components.form.error.min-length", {
                      length: MIN_LENGTH.DESCRIPTION,
                    }),
                    value: MIN_LENGTH.DESCRIPTION,
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH.DESCRIPTION,
                    }),
                    value: MAX_LENGTH.DESCRIPTION,
                  },
                }}
                placeholder={t("page.bookings.create-or-edit.form.section.asset-details.description.placeholder")}
              />
            </FormField>
            {externalProviderOptions.length > 1 && (
              <Tooltip
                tooltip={
                  !canExternalMappingBeEdited &&
                  t("page.bookings.create-or-edit.form.section.asset-details.external-provider.tooltip.disabled")
                }
              >
                <Stack gap="md">
                  <FormField label={t("page.bookings.create-or-edit.form.section.asset-details.external-provider")}>
                    <FormSelect<CreateOrEditAssetFormValues, ExternalBookingProviderDto>
                      name="externalBookingProvider"
                      items={externalProviderOptions}
                      renderOption={getExternalProviderLabel}
                      keySelector={(provider) => provider}
                      disabled={!canExternalMappingBeEdited}
                      onChange={() => {
                        form.setValue("externalResourceId", "", { shouldDirty: true });
                        setCurrExternalSyncStatus("unknown");
                      }}
                    />
                  </FormField>

                  {externalBookingProvider === "outlook" && (
                    <FormField label={t("page.bookings.create-or-edit.form.section.asset-details.room-email")} required>
                      <Stack gap="xs">
                        <Inline gap="sm" align="start" wrap={false}>
                          <FormInput<CreateOrEditAssetFormValues>
                            name="externalResourceId"
                            type="email"
                            disabled={!canExternalMappingBeEdited}
                            placeholder={t(
                              "page.bookings.create-or-edit.form.section.asset-details.room-email.placeholder",
                            )}
                            rules={{
                              validate: {
                                required: createRequiredStringRule(
                                  t,
                                  "page.bookings.create-or-edit.form.section.asset-details.room-email",
                                ),
                                email: createEmailValidRule(t),
                              },
                            }}
                          />
                          {asset?.externalResourceId && currExternalSyncStatus == "unknown" && (
                            <Button
                              icon={<ClockRefreshIcon />}
                              disabled={!externalResourceId}
                              variant="soft"
                              isLoading={checkExternalSyncStatusMutation.isPending}
                              onClick={handleManualSync}
                            >
                              {t("page.bookings.create-or-edit.form.section.asset-details.room-email.btn.manual-sync")}
                            </Button>
                          )}
                          {externalResourceId && currExternalSyncStatus === "green" && (
                            <Badge icon={<CheckIcon />} variant="success" size="lg">
                              {t("page.bookings.create-or-edit.form.section.asset-details.room-email.badge.synced")}
                            </Badge>
                          )}
                          {externalResourceId && currExternalSyncStatus === "failed" && (
                            <Badge icon={<Trash01Icon />} variant="danger" size="lg">
                              {t(
                                "page.bookings.create-or-edit.form.section.asset-details.room-email.badge.sync-failed",
                              )}
                            </Badge>
                          )}
                        </Inline>
                        {currSuggestedEmail && (
                          <p className="flex flex-row items-baseline text-text-secondary">
                            <Trans
                              i18nKey="common.label.email-suggestion"
                              values={{ email: currSuggestedEmail }}
                              components={{
                                button: (value) => (
                                  <span className="ml-space-xs">
                                    <Button
                                      variant="ghostPrimary"
                                      onClick={() => {
                                        form.setValue("externalResourceId", currSuggestedEmail);
                                        form.clearErrors("externalResourceId");
                                        postHog.capture("email_suggestion_clicked", {
                                          email: currSuggestedEmail,
                                        });
                                      }}
                                    >
                                      {value}
                                    </Button>
                                  </span>
                                ),
                              }}
                            />
                          </p>
                        )}
                      </Stack>
                    </FormField>
                  )}

                  {externalBookingProvider === "jim" && (
                    <FormField label={t("page.bookings.create-or-edit.form.section.asset-details.jim-room")} required>
                      <Stack gap="xs">
                        <FormSelect<CreateOrEditAssetFormValues, string>
                          name="externalResourceId"
                          items={jimRoomOptions}
                          renderOption={(id) => jimRooms.find((room) => room.id === id)?.name ?? id}
                          keySelector={(id) => id}
                          disabled={!canExternalMappingBeEdited}
                          placeholder={t(
                            "page.bookings.create-or-edit.form.section.asset-details.jim-room.placeholder",
                          )}
                          rules={{
                            required: t("components.form.error.required", {
                              inputName: t("page.bookings.create-or-edit.form.section.asset-details.jim-room"),
                            }),
                          }}
                        />
                        {externalResourceId && currExternalSyncStatus === "green" && (
                          <Badge icon={<CheckIcon />} variant="success" size="lg">
                            {t("page.bookings.create-or-edit.form.section.asset-details.room-email.badge.synced")}
                          </Badge>
                        )}
                        {externalResourceId && currExternalSyncStatus === "failed" && (
                          <Badge icon={<Trash01Icon />} variant="danger" size="lg">
                            {t("page.bookings.create-or-edit.form.section.asset-details.room-email.badge.sync-failed")}
                          </Badge>
                        )}
                      </Stack>
                    </FormField>
                  )}
                </Stack>
              </Tooltip>
            )}

            {/* Media section */}
            <FormField label={t("page.bookings.create-or-edit.form.section.asset-details.images.label")} required>
              <FormImageInput<CreateOrEditAssetFormValues, "images">
                name="images"
                nOfImages={5}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.bookings.create-or-edit.form.section.asset-details.images.label"),
                  }),
                  validate: {
                    size: createImageSizeRule({
                      t,
                    }),
                    type: createImageTypeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>

            {/* Extra specification section */}
            <FormField label={t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.label")}>
              <Stack gap="md">
                {availableSpecifications.includes("sizeSpecification") && (
                  <Inline gap="sm" align="center" wrap={false}>
                    <LayerSingleIcon tone="secondary" />
                    <FormInput<CreateOrEditAssetFormValues>
                      data-testid="size-spec-input"
                      name="sizeSpecification"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder={t(
                        "page.bookings.create-or-edit.form.section.asset-details.extra-specifications.size.placeholder",
                      )}
                      width="numeric"
                    />
                    <label>
                      {t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.size.label")}
                    </label>
                    <IconButton
                      variant="filled"
                      tone="error"
                      title={t("common.action.delete")}
                      onClick={() => handleDeleteSpecification("sizeSpecification")}
                      icon={<Trash01Icon />}
                    />
                  </Inline>
                )}
                {availableSpecifications.includes("pricePerHourSpecification") ? (
                  <Inline gap="sm" align="center" wrap={false}>
                    <CurrencyEuroIcon tone="secondary" />
                    <FormInput<CreateOrEditAssetFormValues>
                      data-testid="price-spec-input"
                      name="pricePerHourSpecification"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder={t(
                        "page.bookings.create-or-edit.form.section.asset-details.extra-specifications.price.placeholder",
                      )}
                      width="numeric"
                    />
                    <label>
                      {t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.price.label")}
                    </label>
                    <IconButton
                      variant="filled"
                      tone="error"
                      title={t("common.action.delete")}
                      onClick={() => handleDeleteSpecification("pricePerHourSpecification")}
                      icon={<Trash01Icon />}
                    />
                  </Inline>
                ) : null}
                {availableSpecifications.includes("capacitySpecification") ? (
                  <Inline gap="sm" align="center" wrap={false}>
                    <Users01Icon tone="secondary" />
                    <FormInput<CreateOrEditAssetFormValues>
                      data-testid="capacity-spec-input"
                      name="capacitySpecification"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      placeholder={t(
                        "page.bookings.create-or-edit.form.section.asset-details.extra-specifications.capacity.placeholder",
                      )}
                      width="numeric"
                    />
                    <label>
                      {t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.capacity.label")}
                    </label>
                    <IconButton
                      variant="filled"
                      tone="error"
                      title={t("common.action.delete")}
                      onClick={() => handleDeleteSpecification("capacitySpecification")}
                      icon={<Trash01Icon />}
                    />
                  </Inline>
                ) : null}
                {availableSpecifications.includes("locationSpecification") ? (
                  <Inline gap="sm" align="center" wrap={false}>
                    <MarkerPin01Icon tone="secondary" />
                    <FormInput<CreateOrEditAssetFormValues>
                      data-testid="location-spec-input"
                      name="locationSpecification"
                      placeholder={t(
                        "page.bookings.create-or-edit.form.section.asset-details.extra-specifications.location.placeholder",
                      )}
                      width="full"
                    />
                    <IconButton
                      variant="filled"
                      tone="error"
                      title={t("common.action.delete")}
                      onClick={() => handleDeleteSpecification("locationSpecification")}
                      icon={<Trash01Icon />}
                    />
                  </Inline>
                ) : null}
                <Button
                  data-testid="add-specification-btn"
                  variant="soft"
                  onClick={extraSpecificationsModalOpenHandler.setTrue}
                  disabled={availableSpecifications.length === 4}
                >
                  {t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.add")}
                </Button>
              </Stack>
            </FormField>

            {/* Asset regulations section */}
            <span className="text-heading-3">{t("page.bookings.create-or-edit.form.section.asset-regulations")}</span>
            <FormCheckbox<CreateOrEditAssetFormValues, "regulationsApplicable">
              data-testid="regulations-checkbox"
              name="regulationsApplicable"
              label={t("page.bookings.create-or-edit.form.section.asset-regulations.regulations-applicable.label")}
            />
            {regulationsApplicable && (
              <FormDocumentInput<CreateOrEditAssetFormValues, "regulationDocument">
                name="regulationDocument"
                accept={ALLOWED_FILE_TYPES.DOCUMENT}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.bookings.create-or-edit.form.section.asset-regulations.document.name"),
                  }),
                  validate: {
                    size: createDocumentSizeRule({
                      t,
                    }),
                    type: createDocumentTypeRule({
                      t,
                    }),
                  },
                }}
              />
            )}

            {/* Asset details section */}
            <span className="text-heading-3">{t("page.bookings.create-or-edit.form.section.booking-details")}</span>
            <fieldset className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs">
              <label>{t("page.bookings.create-or-edit.form.section.booking-details.amount-seats.label.1")}</label>
              <FormInput<CreateOrEditAssetFormValues, "seats">
                name="seats"
                data-testid="amount-seats-input"
                placeholder={(defaultValues.seats || MIN_AMOUNT_SEATS).toString()}
                type="number"
                inputMode="numeric"
                min={defaultValues.seats || MIN_AMOUNT_SEATS}
                rules={{
                  valueAsNumber: true,
                  validate: {
                    min(value) {
                      if (!value) {
                        return;
                      }

                      return value < MIN_AMOUNT_SEATS
                        ? t("page.bookings.create-or-edit.form.section.booking-details.amount-seats.error.min")
                        : undefined;
                    },
                  },
                }}
                width="numeric"
                onChange={(e) => {
                  if (e.target.valueAsNumber != null && e.target.valueAsNumber > 1) {
                    form.setValue("canBookMultipleSlots", false, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }
                }}
              />
              <label>{t("page.bookings.create-or-edit.form.section.booking-details.amount-seats.label.2")}</label>
            </fieldset>
            <Stack gap="sm">
              <FormField label={t("page.bookings.create-or-edit.form.section.booking-details.audience.label")} required>
                <FormSelect<CreateOrEditAssetFormValues, CreateOrEditAssetFormValues["assetObserver"]>
                  name="assetObserver"
                  items={assetObservers}
                  renderOption={(item) => item.name}
                  keySelector={(item) => item.id}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.bookings.create-or-edit.form.section.booking-details.audience.name"),
                    }),
                  }}
                  disabled={isEditMode || assetObservers.length === 1}
                  placeholder={t("page.bookings.create-or-edit.form.section.booking-details.audience.placeholder")}
                  onChange={(item) => {
                    if (item?.type === "projectConnection") {
                      form.setValue("audience", []);
                    }
                  }}
                />
              </FormField>
              {sessionUser.isAdmin && assetObserver.type === "project" && (
                <AudienceSelector
                  defaultAudience={audience}
                  editWarningMessage={t("component.audience-selector.edit-warning.asset")}
                  onSave={(newAudience) => form.setValue("audience", newAudience)}
                  {...{
                    isEditMode,
                  }}
                />
              )}
            </Stack>

            <Stack gap="lg">
              {/* Asset bookable schedule section */}
              <FormField
                label={t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.label")}
                tooltip={
                  canEditTimeslot
                    ? ""
                    : t(
                        "page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.timeslot.disabled-tooltip",
                      )
                }
                required
              >
                <Stack gap="sm">
                  <FormSelect<CreateOrEditAssetFormValues, CreateOrEditAssetFormValues["timeslot"]>
                    name="timeslot"
                    items={timeSlotOptions}
                    renderOption={getTimeslotOptionLabel}
                    keySelector={(x) => x || "none"}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t(
                          "page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.name",
                        ),
                      }),
                    }}
                    placeholder={t(
                      "page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.placeholder",
                    )}
                    disabled={!canEditTimeslot}
                  />
                  {slot === "custom" && (
                    <AssetCustomTimeslotInput
                      value={customTimeslot}
                      onChange={(value) =>
                        form.setValue("customTimeslot", value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }
                      isDisabled={!canEditTimeslot}
                    />
                  )}
                </Stack>
              </FormField>
              <Notice
                variant="special"
                icon={<ClockCheckIcon />}
                message={t("page.bookings.create-or-edit.form.section.booking-details.bookable-timeslots.remark")}
              />
            </Stack>

            <FormField
              label={t("page.bookings.create-or-edit.form.section.booking-details.bookable-days.label")}
              required
            >
              <FormErrorWrapper<CreateOrEditAssetFormValues> name="bookableDays" encircle>
                <Stack gap="sm">
                  {bookableDays.map((bookableDay, bookableDayIndex) => {
                    return (
                      <AssetBookableDaySelector
                        key={bookableDay.day}
                        bookableDay={bookableDay}
                        bookableDayIndex={bookableDayIndex}
                        onChangeStartTime={(bookableTimeIndex, value) =>
                          handleChangeTime(bookableDayIndex, bookableTimeIndex, value, "start")
                        }
                        onChangeEndTime={(bookableTimeIndex, value) =>
                          handleChangeTime(bookableDayIndex, bookableTimeIndex, value, "end")
                        }
                      />
                    );
                  })}
                </Stack>
              </FormErrorWrapper>
            </FormField>

            {/* Booking notifications section */}
            <span className="text-heading-3">
              {t("page.bookings.create-or-edit.form.section.booking-notifications")}
            </span>
            <Inline gap="sm" align="start" wrap={false}>
              <div className="flex min-h-control-md items-center">
                <FormCheckbox<CreateOrEditAssetFormValues, "notificationEnabled">
                  name="notificationEnabled"
                  label=""
                  id="notificationEnabled"
                />
              </div>
              <fieldset className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs">
                <label htmlFor="notificationEnabled">
                  {t("page.bookings.create-or-edit.form.section.booking-notifications.send-email.1")}
                </label>
                <FormInput<CreateOrEditAssetFormValues, "notificationEmail">
                  name="notificationEmail"
                  placeholder={t("page.bookings.create-or-edit.form.section.booking-notifications.email.placeholder")}
                  type="email"
                  inputMode="email"
                  rules={{
                    validate: {
                      required: () => {
                        const values = form.getValues();
                        if (!values.notificationEnabled) {
                          return;
                        }
                        if (!values.notificationEmail) {
                          return t("components.form.error.required", {
                            inputName: t("page.bookings.create-or-edit.form.section.booking-notifications.email.name"),
                          });
                        }
                      },
                      isValid: createEmailValidRule(t),
                    },
                  }}
                  disabled={!notificationEnabled}
                />
                <label>{t("page.bookings.create-or-edit.form.section.booking-notifications.send-email.2")}</label>
              </fieldset>
            </Inline>

            {/* Booking restrictions section */}
            <span className="text-heading-3">
              {t("page.bookings.create-or-edit.form.section.booking-restrictions")}
            </span>
            <FormCheckbox<CreateOrEditAssetFormValues, "canBookSameDay">
              name="canBookSameDay"
              label={t("page.bookings.create-or-edit.form.section.booking-restrictions.book-same.day.label")}
            />
            <span className="flex gap-space-sm">
              <FormCheckbox<CreateOrEditAssetFormValues, "canBookMultipleSlots">
                name="canBookMultipleSlots"
                id="canBookMultipleSlots"
                disabled={!!seats && seats > 1}
                label={t("page.bookings.create-or-edit.form.section.booking-details.booking-restriction.label")}
              />
              <Info
                tooltip={t("page.bookings.create-or-edit.form.section.booking-details.booking-restriction.tooltip")}
              />
            </span>
            <fieldset className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs">
              <label>
                {t("page.bookings.create-or-edit.form.section.booking-restrictions.advance-booking.label.1")}
              </label>
              <FormInput<CreateOrEditAssetFormValues, "maxDaysInAdvance">
                name="maxDaysInAdvance"
                placeholder={t(
                  "page.bookings.create-or-edit.form.section.booking-restrictions.advance-booking.placeholder",
                )}
                type="number"
                inputMode="numeric"
                min={MIN_ADVANCE_BOOKING_DAYS}
                max={MAX_ADVANCE_BOOKING_DAYS}
                rules={{
                  valueAsNumber: true,
                  validate: {
                    min(value) {
                      if (!value) {
                        return;
                      }

                      return Number(value) < MIN_ADVANCE_BOOKING_DAYS
                        ? t("page.bookings.create-or-edit.form.section.booking-restrictions.advance-booking.error.min")
                        : undefined;
                    },
                    max(value) {
                      if (!value) {
                        return;
                      }

                      return Number(value) > MAX_ADVANCE_BOOKING_DAYS
                        ? t("page.bookings.create-or-edit.form.section.booking-restrictions.advance-booking.error.max")
                        : undefined;
                    },
                  },
                }}
                width="numeric"
              />
              <label>
                {t("page.bookings.create-or-edit.form.section.booking-restrictions.advance-booking.label.2")}
              </label>
            </fieldset>
            <fieldset className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs">
              <label>{t("page.bookings.create-or-edit.form.section.booking-restrictions.edit-booking.label.1")}</label>
              <FormInput<CreateOrEditAssetFormValues, "hoursForMutations">
                name="hoursForMutations"
                placeholder={t(
                  "page.bookings.create-or-edit.form.section.booking-restrictions.edit-booking.placeholder",
                )}
                type="number"
                inputMode="numeric"
                min={MIN_EDIT_BOOKING_HOURS}
                rules={{
                  validate: {
                    min(value) {
                      if (!value) {
                        return;
                      }

                      return Number(value) < MIN_EDIT_BOOKING_HOURS
                        ? t("page.bookings.create-or-edit.form.section.booking-restrictions.edit-booking.error.min")
                        : undefined;
                    },
                  },
                }}
                width="numeric"
              />
              <label>{t("page.bookings.create-or-edit.form.section.booking-restrictions.edit-booking.label.2")}</label>
            </fieldset>
            <Stack gap="xs">
              <label>{t("page.bookings.create-or-edit.form.section.booking-restrictions.available-from.label")}</label>
              <FormScheduleInput<CreateOrEditAssetFormValues, "availableFrom">
                name="availableFrom"
                min={minScheduled}
                canEdit
                labelWhenNoValue={t(
                  "page.bookings.create-or-edit.form.section.booking-restrictions.available-from.placeholder",
                )}
                rules={{
                  validate: {
                    laterThanPublishDate: (date) => {
                      if (!date) {
                        return undefined;
                      }
                      const publishAt = form.getValues("publishAt");
                      if (!publishAt) {
                        return undefined;
                      }

                      return date < publishAt
                        ? t(
                            "page.bookings.create-or-edit.form.section.booking-restrictions.available-from.validation.must-be-later-than-publish",
                          )
                        : undefined;
                    },
                  },
                }}
              />
            </Stack>
            <Stack gap="xs">
              <label>{t("page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.label")}</label>
              <FormScheduleInput<CreateOrEditAssetFormValues, "unpublishAt">
                name="unpublishAt"
                min={minScheduled}
                canEdit
                labelWhenNoValue={t(
                  "page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.placeholder",
                )}
                rules={{
                  validate: {
                    laterThanMin: (date) => {
                      if (!date) {
                        return undefined;
                      }

                      return date < minScheduled
                        ? t(
                            "page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.validation.must-be-in-future",
                          )
                        : undefined;
                    },
                    laterThanPublishDate: (date) => {
                      if (!date) {
                        return undefined;
                      }
                      const publishAt = form.getValues("publishAt");
                      if (!publishAt) {
                        return undefined;
                      }

                      return date < publishAt
                        ? t(
                            "page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.validation.must-be-later-than-publish",
                          )
                        : undefined;
                    },
                    laterThanAvailableFrom: (date) => {
                      if (!date) {
                        return undefined;
                      }
                      const availableFrom = form.getValues("availableFrom");
                      if (!availableFrom) {
                        return undefined;
                      }

                      return date < availableFrom
                        ? t(
                            "page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.validation.must-be-later-than-available-from",
                          )
                        : undefined;
                    },
                    canBookSameDayMinDaysRestriction: (date, formValues) => {
                      if (!date) {
                        return undefined;
                      }
                      if (formValues.canBookSameDay) {
                        return undefined;
                      }
                      const availableFrom = formValues.availableFrom;
                      if (!availableFrom) {
                        return undefined;
                      }
                      if (date < addDays(availableFrom, 2)) {
                        return t(
                          "page.bookings.create-or-edit.form.section.booking-restrictions.unpublish.validation.min-days-when-not-book-same-day",
                        );
                      }
                    },
                  },
                }}
              />
            </Stack>
            <FormCheckbox<CreateOrEditAssetFormValues, "requireBookingReason">
              name="requireBookingReason"
              label={t("page.bookings.create-or-edit.form.section.booking-restrictions.booking-reason.label")}
            />
            {/* Publish settings section */}
            {!isPublished && (
              <>
                {/* Publish Section */}
                <span className="text-heading-3">{t("page.bookings.create-or-edit.form.section.publish")}</span>
                <FormScheduleInput
                  name="publishAt"
                  min={minScheduled}
                  canEdit={!isEditMode || !isPublished}
                  rules={{
                    validate: {
                      laterThanMin: (date) => {
                        if (!date) {
                          return undefined;
                        }

                        return date < minScheduled
                          ? t("page.bookings.create-or-edit.form.section.publish.publish.validation.must-be-in-future")
                          : undefined;
                      },
                    },
                  }}
                />
              </>
            )}
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" data-testid="submit-asset-btn" isLoading={isSubmitting}>
                {t("common.action.save")}
              </Button>
            )}
            renderSecondaryAction={() => (
              <Button variant="subtle" tone="error" disabled={isSubmitting} onClick={cancelModalHandler.setTrue}>
                {t("common.action.cancel")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
      <ExtraSpecificationsModal
        isOpened={isExtraSpecificationsModalOpened}
        onOpenChange={extraSpecificationsModalOpenHandler.set}
        availableSpecifications={availableSpecifications}
        onAddSpecifications={handleAddSpecifications}
      />
      <ConfirmModal
        tone="primary"
        isOpened={isCancelModalOpen}
        data-testid="cancel-modal"
        title={t("page.bookings.create-or-edit.cancel-modal.title")}
        description={t("page.bookings.create-or-edit.cancel-modal.description")}
        shouldCloseOnEsc
        onOpenChange={cancelModalHandler.set}
        onReject={cancelModalHandler.setFalse}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={() =>
          void navigate(
            routes.bookings.list({
              slug,
            }),
          )
        }
        resolveBtnProps={{
          text: t("common.action.confirm"),
        }}
        isLoading={false}
      />
      <ConfirmModal
        isOpened={bookingsToBeCancelled !== null}
        title={t("page.bookings.create-or-edit.edit-open-time-modal.title")}
        description={t("page.bookings.create-or-edit.edit-open-time-modal.description", {
          count: bookingsToBeCancelled || 0,
        })}
        isLoading={false}
        tone="warning"
        onOpenChange={(state) => {
          if (!state) {
            setBookingsToBeCancelled(null);
          }
        }}
        onReject={() => setBookingsToBeCancelled(null)}
        rejectBtnProps={{
          "data-testid": "confirm-edit-open-time-cancel",
        }}
        onResolve={async () => {
          setBookingsToBeCancelled(null);
          await onSubmit(form.getValues());
        }}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "confirm-edit-open-time-confirm",
        }}
        shouldCloseOnEsc
        data-testid="confirm-edit-open-time-modal"
      />
    </DocumentPage>
  );
};
